import User from "@/data/models/User";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { switchTargetUserAccess } from "@/permissions/UserPermissions";
import { RoleSettingsModule } from "@/store/modules/RoleSettingsModule";
import { TargetUserModule } from "@/store/modules/TargetUserModule";
import { UserModule } from "@/store/modules/UserModule";
import { resolveViewAs, ViewAsResolution } from "@/utils/TargetUserSelection";
import {
  clearViewAsSelection,
  readViewAsSelection,
  storeViewAsSelection,
} from "@/utils/ViewAsSession";
import { clearViewAsBranchFilters } from "@/utils/BranchFilterStorage";

export { isViewAsSelected } from "@/store/modules/TargetUserModule";

/**
 * "Sicht auf" (view as): one choice for the whole app, kept per browser tab across a refresh
 * (ticket 2610050). Screens read it here, never from the store module or a route query.
 * Rules: docs/users/view-as.md.
 */

/** The stored choice resolved against the live users and the viewer's rights (reactive). */
export function getViewAsResolution(): ViewAsResolution {
  // Every reactive input is read before any early return, so a computed built on this always
  // re-evaluates when one of them changes.
  const targetUserId = TargetUserModule.userId;
  const viewer = getReactiveActiveUser();
  const users = UserModule.users;
  const areRightsKnown = RoleSettingsModule.hasLoaded;
  const mayUseViewAs = !!viewer && switchTargetUserAccess(viewer);

  return resolveViewAs(targetUserId, viewer, users, mayUseViewAs, areRightsKnown);
}

/** The user the app is shown as, or `null` for the viewer's own view (reactive). */
export function getViewAsUser(): User | null {
  return getViewAsResolution().user;
}

/**
 * The user whose view the app shows: the chosen user while "Sicht auf" is active, the signed-in
 * user otherwise (reactive). Use it for what a screen shows and loads: page and button visibility,
 * "my" items, branches (ticket 2610052). Keep `getReactiveActiveUser()` for anything about the
 * signed-in person: session, own profile, switching client or user, who may use "Sicht auf".
 */
export function getEffectiveUser(): User | null {
  return getViewAsUser() ?? getReactiveActiveUser();
}

/** Shows the app as `user`, in this tab, until it is ended or the session changes. */
export function startViewAs(user: User) {
  const viewer = getReactiveActiveUser();

  if (!viewer) {
    return;
  }

  storeViewAsSelection(viewer.id, user.id);
  // A new choice starts on the chosen user's default branch, not the previous one's.
  clearViewAsBranchFilters();
  TargetUserModule.setUserId(user.id);
}

/** Returns to the viewer's own view. */
export function endViewAs() {
  clearViewAsSelection();
}

/**
 * Restores the choice kept for this tab after a page load. `Scaffold` calls it once it is created:
 * every auth event, the page load's included, clears the in-memory choice first.
 */
export function restoreViewAs() {
  const targetUserId = readViewAsSelection(getReactiveActiveUser()?.id);

  if (targetUserId) {
    TargetUserModule.setUserId(targetUserId);
  }
}
